import { createSlice } from '@reduxjs/toolkit';
import { CouponData, CouponStatusType } from '@src/types/coupon';
import {
  createCoupon,
  deleteCoupon,
  getCouponList,
  updateCoupon,
  updateCouponStatus,
} from './couponListThunk';
import { CouponListModalType } from '../types';

interface CouponListState {
  loading: boolean;
  optionSearchLoading: boolean;
  coupons: CouponData[];
  options: { value: string; label: string }[];
  modal: CouponListModalType[];
  selected?: { name: string; id: string };
  meta: {
    itemsPerPage?: number;
    totalItems?: number;
    currentPage?: number;
    totalPages?: number;
  };
  params: {
    page: number;
    limit: number;
    search: string;
    filter?: { status?: CouponStatusType };
  };
}

const initialState: CouponListState = {
  loading: false,
  optionSearchLoading: false,
  coupons: [],
  options: [],
  modal: [],
  meta: {},
  params: {
    page: 1,
    limit: 20,
    search: '',
    filter: {},
  },
};

const couponsSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    onChangeSearchValue(state, { payload }) {
      state.params.search = payload;
    },
    onChangeFilterValue(state, { payload }) {
      state.params.filter.status = payload;
    },
    onChangePage(state, { payload }) {
      state.params.page = payload;
    },
    onChangeModal(state, { payload }) {
      if (payload === 'off') {
        state.modal = [];
      } else if (state.modal.includes(payload)) {
        state.modal = state.modal.filter((modal) => modal !== payload);
      } else {
        state.modal = [...state.modal, payload];
      }
    },
    onActionSelected(state, { payload }) {
      state.selected = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCouponList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCouponList.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.coupons = payload.data;
        state.meta = payload.meta;
      })
      .addCase(getCouponList.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(updateCouponStatus.fulfilled, (state, { payload }) => {
        const idx = state.coupons.findIndex(
          (coupon) => coupon.id === payload.id
        );
        state.coupons[idx].status = payload.status;
        state.coupons = [...state.coupons];
      })
      .addCase(updateCouponStatus.rejected, (_, { payload }) => {
        throw payload;
      });

    builder
      .addCase(deleteCoupon.fulfilled, (state, { payload }) => {
        state.coupons = state.coupons.filter((coupon) => coupon.id !== payload);
      })
      .addCase(deleteCoupon.rejected, (_, { payload }) => {
        throw payload;
      });

    builder.addCase(createCoupon.rejected, (_, { payload }) => {
      throw payload;
    });

    builder.addCase(updateCoupon.rejected, (_, { payload }) => {
      throw payload;
    });
  },
});

export const {
  onChangeSearchValue,
  onChangeFilterValue,
  onChangePage,
  onChangeModal,
  onActionSelected,
} = couponsSlice.actions;
export default couponsSlice.reducer;
